import { getApiClient, browserCacheBuster } from '@/lib/api/api-client';
import { Publication } from '@/types/publication';
import { CatalogItem, PaginatedResponse, BaseQueryParams, WhereFilter, getSortValue } from './catalog-types';
import { CacheService } from '../cache';
import { writingTransformer } from '@/lib/transformers/writings.transformer';
import { handleApiError, success, type ApiResult } from '@/lib/utils/errorHandler';
import { logger } from '@/lib/utils/logger';
import { getCacheDuration } from '@/lib/config/cache.config';

export interface FindWritingsParams extends Omit<BaseQueryParams, 'category'> {
  years?: string[];
  category?: string[];
}

/**
 * Transform Publication to CatalogItem
 */
export function publicationToCatalogItem(publication: Publication): CatalogItem {
  return writingTransformer.toCatalogItem(publication);
}

/**
 * Writings API service
 */
export class WritingsAPI {
  static apiClient = getApiClient();
  static CACHE_DURATION = getCacheDuration('writings');

  /**
   * Get writings list
   */
  static async findWritings(
    params: FindWritingsParams & Record<string, unknown>
  ): Promise<ApiResult<PaginatedResponse<CatalogItem>>> {
    const { page = 1, limit = 10, query, sort, years, category, ...rest } = params;

    const sortValue = getSortValue(sort, { dateField: 'publishDate' });

    // Build where query
    const where: WhereFilter = {
      status: {
        equals: 'published',
      },
    };

    // Search query
    if (query) {
      where.title = {
        contains: query,
      };
    }

    // Year filter
    if (years && years.length > 0) {
      try {
        if (years.length === 1) {
          where.publishDate = {
            contains: years[0],
          };
        } else {
          where.or = years.map(year => ({
            publishDate: {
              contains: year,
            },
          }));
        }
      } catch (error) {
        logger.error('Error processing years:', error);
      }
    }

    // Category filter
    if (category && category.length > 0) {
      try {
        if (category.length === 1) {
          where['category.title'] = {
            equals: category[0],
          };
        } else {
          where.or = category.map(cat => ({
            'category.title': {
              equals: cat,
            },
          }));
        }
      } catch (error) {
        logger.error('Error processing categories:', error);
      }
    }

    // Additional where parameters
    if (rest.where) {
      Object.assign(where, rest.where);
    }

    try {

      const response = await WritingsAPI.apiClient.collections.publications.find({
        page,
        limit,
        where,
        depth: params.depth || 2,
        // TODO: type properly - getSortValue returns dynamic strings not in payload-rest-client's keyof DOC union
        sort: sortValue as never,
        ...browserCacheBuster(),
      });

      // Transform API response
      const result = response as PaginatedResponse<Publication>;

      // Transform to CatalogItem
      const items = result.docs.map(publicationToCatalogItem);

      return success({
        docs: items,
        totalDocs: result.totalDocs,
        limit: result.limit,
        totalPages: result.totalPages,
        page: result.page,
        pagingCounter: result.pagingCounter,
        hasPrevPage: result.hasPrevPage,
        hasNextPage: result.hasNextPage,
        prevPage: result.prevPage,
        nextPage: result.nextPage,
      });
    } catch (error) {
      return handleApiError('findWritings', error);
    }
  }

  /**
   * Get writing by slug
   */
  static async getBySlug(slug: string, skipCache: boolean = false): Promise<ApiResult<Publication>> {
    const cacheKey = await CacheService.scopedKey(`writings.service_${slug}`);
    // Check cache (if skipCache is false)
    if (!skipCache) {
      const cachedData = CacheService.getFromCache(cacheKey);
      if (cachedData) {
        return success(cachedData as Publication);
      }
    }
    try {
      const response = await WritingsAPI.apiClient.collections.publications.find({
        where: {
          slug: {
            equals: slug,
          },
          status: {
            equals: 'published',
          },
        },
        limit: 1,
        depth: 2,
      });

      const publication = response.docs[0] || null;
      if (publication) {
        // Write to cache
        CacheService.addToCache(cacheKey, publication, WritingsAPI.CACHE_DURATION);
      }

      return success(publication);
    } catch (error) {
      return handleApiError('getBySlug', error);
    }
  }

  /**
   * Get writing by ID
   */
  static async getById(id: string | number): Promise<ApiResult<Publication>> {
    const cacheKey = await CacheService.scopedKey(`writings.service_${id}`);
    // Check cache
    const cachedData = CacheService.getFromCache(cacheKey);
    if (cachedData) {
      return success(cachedData as Publication);
    }
    try {
      const response = await WritingsAPI.apiClient.collections.publications.findById({
        id: Number(id),
        depth: 2,
      });

      if (response) {
        // Write to cache
        CacheService.addToCache(cacheKey, response, WritingsAPI.CACHE_DURATION);
      }
      return success(response || null);
    } catch (error) {
      return handleApiError('getById', error);
    }
  }

  /**
   * Get writing detail by slug
   */
  static async findWritingBySlug(slug: string): Promise<ApiResult<Publication>> {
    const cacheKey = await CacheService.scopedKey(`writings.service_${slug}`);
    // Check cache
    const cachedData = CacheService.getFromCache(cacheKey);
    if (cachedData) {
      return success(cachedData as Publication);
    }
    try {
      const response = await WritingsAPI.apiClient.collections.publications.find({
        where: {
          slug: {
            equals: slug,
          },
          status: {
            equals: 'published',
          },
        },
        depth: 2,
        limit: 1,
      });

      const publication = response.docs.length > 0 ? response.docs[0] : null;

      if (publication) {
        // Write to cache
        CacheService.addToCache(cacheKey, publication, WritingsAPI.CACHE_DURATION);
      }

      return success(publication);
    } catch (error) {
      return handleApiError('findWritingBySlug', error);
    }
  }
}
